<template>
  <div :class="[previewFile ? 'preview' : '', 'modal-container', 'card-elevated']">
    <div class="card-header">
      <div class="header-text">{{ cardName }}</div>
      <span class="material-symbols-outlined close" @click="$emit('close-upload-form', false)">close</span>
    </div>
    <div :class="[previewFile ? 'preview' : '', 'modal-body']">
      <div class="left">
        <div class="form-block">
          <div class="input-block">
            <label for="reportType" class="label">Report Type
            </label>
            <select id="reportType" class="text-input" v-model="typeSelect" @change="changeType" required>
              <option v-for="reportType in reportTypes" :key="reportType">
                {{ reportType }}</option>
            </select>
          </div>
          <div class="input-block" v-show="isGenomics || isPathology || isRadiology">
            <label for="specimenType" class="label">Specimen Type</label>
            <input maxlength="100" id="specimenType" v-model="specimenType" class="text-input" required
              @keyup="checkUpload">
          </div>
          <div class="input-block">
            <label for="notes" class="label">{{ descriptionLabelText }}</label>
            <input maxlength="100" id="notes" v-model="description" class="text-input" required @keyup="checkUpload">
          </div>
          <div class="input-block">
            <label for="accessionNum" class="label">Accession #</label>
            <input maxlength="100" id="accessionNum" v-model="accessionNum" class="text-input" @keyup="checkUpload">
          </div>
          <div class="input-block">
            <label for="labName" class="label">Lab Name</label>
            <input maxlength="100" id="labName" v-model="labName" class="text-input" @keyup="checkUpload">
          </div>
          <div class="input-block">
            <label for="reportOrderDate" class="label">{{ dateLabelText }}</label>
            <input maxlength="100" type="date" v-model="eventDate" id="reportOrderDate" class="text-input"
              :max="maxDate" @click="recalculateMaxDate" @select="onInputChange" @change="checkUpload" />
          </div>
        </div>
        <div class="upload-block">
          <div class="input-block" v-show="!itemToEdit?.uuid">
            <!-- upload area -->
            <DropZone class="drop-area upload-box" @files-dropped="addFiles" #default="{ dropZoneActive }"
              @click="openFileUpload">
              <label for="file-input">
                <span v-if="dropZoneActive">
                  <span>Drop them here to add them</span>
                </span>
                <span v-else>
                  <span>Drag Files Here or Click to Select</span>
                </span>
              </label>
              <ul class="image-list" v-show="files.length">
                <FilePreview v-for="(file, index) in files" :key="file.id" :file="file"
                  :isSelected="activePreviewIndex === index" tag="li" @remove="removeFile"
                  @open-file-preview="togglePreview(index)" />
              </ul>
            </DropZone>
          </div>
          <div v-show="!itemToEdit?.uuid" class="upload-tips" :class="{ highlight: wrongFileType }">
            Only .pdf and .txt files allowed.
          </div>
          <div class="submit-block">
            <button @click.prevent="itemToEdit?.uuid
    ? editFile()
    : uploadFiles(files, uploadFileMetadataURL, patientId)" class="primary-filled-button"
              :disabled="!okToUpload">Submit</button>
          </div>
        </div>
        <input type="file" ref="fileInputRef" accept=".pdf,.txt,plain/text,application/pdf" multiple
          @change="addFiles($event.target.files)" class="hidden-input" />
      </div>
      <div v-if="previewFile" class="right">
        <span v-if="activePreviewIndex > 0" @click="prevPreviewFile"
          class="material-symbols-outlined hover-effect preview-nav-button chevron-left">chevron_left</span>
        <span v-if="activePreviewIndex < files.length - 1" @click="nextPreviewFile"
          class="material-symbols-outlined hover-effect preview-nav-button chevron-right">chevron_right</span>
        <embed :src="`${previewFile.url}#toolbar=0`" type="application/pdf" frameBorder="0" scrolling="auto"
          height="100%" width="100%" />
      </div>
    </div>
  </div>
</template>

<script setup>
// Copyright (c) 2020-2023 Omics Data Automation, Inc.
// Copyright (c) 2023 datma, inc.
//
// Proprietary and Confidential:
// Unauthorized copying of this file by any medium is strictly prohibited.

import { computed, defineEmits, defineProps, onMounted, reactive, ref, watch } from 'vue'
import { useStore } from 'vuex'
import { ehrCrudUrl, fileSizeLimit } from '@/settings'
import { fetchWithTimeout } from '@/common/shared'
import DropZone from './DropZone.vue'
import FilePreview from './FilePreview.vue'
import editReportFile from './editReportFile'
import { showToast } from '../../../utils/utils'
import moment from 'moment'

const uploadFileMetadataURL = `${ehrCrudUrl}/report-store/patient-identifier/` // + identifier

const patientId = computed(() => store.getters.session?.patient?.id || '')

const reportTypesBaseUrl = `${ehrCrudUrl}/report-types`

const store = useStore()
//const tr = (x) => x // inject('tr')

const dateLabelText = ref('Ordering Date')
const descriptionLabelText = ref('Notes')
const isGenomics = ref(false)
const isRadiology = ref(false)
const isPathology = ref(false)
const reportTypes = reactive([])
const typeSelect = ref('')
const specimenType = ref('')
const description = ref('')
const eventDate = ref('')
const accessionNum = ref('')
const labName = ref('')
const okToUpload = ref('false')
const files = ref([])
const fileInputRef = ref(null)
const wrongFileType = ref(false)
const previewFile = computed(() => files.value?.[activePreviewIndex.value])
const activePreviewIndex = ref(null)

const togglePreview = (index) => activePreviewIndex.value = activePreviewIndex.value === index ? null : index
const nextPreviewFile = () => activePreviewIndex.value += 1
const prevPreviewFile = () => activePreviewIndex.value -= 1

const getTodaysDate = () => {
  const currentDate = new Date()
  currentDate.setDate(currentDate.getDate() + 1) // need to set date to tomorrow to avoid timezone issues
  return currentDate.toISOString().split('T')[0]
}
const recalculateMaxDate = () => {
  maxDate.value = getTodaysDate()
}
const maxDate = ref(getTodaysDate())

const props = defineProps({
  cardName: { type: String, required: true },
  showUpload: { type: Boolean, default: true },
  itemToEdit: { type: Object, default: null },
})

watch(() => props.itemToEdit, (newValue) => {
  // Recieves an empty object when upload is clicked
  if (!newValue?.uuid) {
    clearInputs()
    return
  }
  // Set the fields
  typeSelect.value = newValue?.report_type?.short_name
  specimenType.value = newValue?.identifiers.find((i) => i.identifier_type === 'specimen_type')?.identifier || ''
  accessionNum.value = newValue?.identifiers.find((i) => i.identifier_type === 'accession_num')?.identifier || ''
  labName.value = newValue?.identifiers.find((i) => i.identifier_type === 'lab_name')?.identifier || ''
  description.value = newValue?.description
  eventDate.value = newValue?.ordering_date?.split('T')[0]
  // Set the type
  changeType(typeSelect)
})

const emit = defineEmits(['close-upload-form', 'refresh-diagnostics-tab', 'update-list'])

const openFileUpload = () => {
  fileInputRef.value.click()
}

const changeType = () => {
  clearTypes()
  switch (typeSelect.value) {
    case 'GENOMICS':
      dateLabelText.value = 'Specimen collected on'
      descriptionLabelText.value = 'Test Type'
      isGenomics.value = true
      break
    case 'PATHOLOGY':
      dateLabelText.value = 'Specimen collected on'
      descriptionLabelText.value = 'Test Type'
      isPathology.value = true
      break
    case 'RADIOLOGY':
      dateLabelText.value = 'Imaging date'
      descriptionLabelText.value = 'Image Type'
      isRadiology.value = true
      break
    default:
      dateLabelText.value = 'Ordering date'
      descriptionLabelText.value = 'Notes'
  }
  checkUpload() // Need to recheck when type changes
}

const clearTypes = () => {
  // Clincal is the default inside the changeType switch
  isGenomics.value = false
  isRadiology.value = false
  isPathology.value = false
}

const valuesHaveChanged = (shouldCheckSpecimenType) => {
  const checks = [
    { value: description.value, original: props.itemToEdit.description },
    { value: eventDate.value, original: props.itemToEdit.ordering_date?.split('T')[0] },
    {
      value: accessionNum.value,
      original: props.itemToEdit?.identifiers.find((i) => i.identifier_type === 'accession_num')?.identifier || '',
    },
    {
      value: labName.value,
      original: props.itemToEdit?.identifiers.find((i) => i.identifier_type === 'lab_name')?.identifier || '',
    },
    {
      value: typeSelect.value,
      original: props.itemToEdit?.report_type?.short_name,
    },
    // Spread it in if it needs to be checked
    ...(shouldCheckSpecimenType ? [
      {
        value: specimenType.value,
        original: props.itemToEdit?.identifiers.find((i) => i.identifier_type === 'specimen_type')?.identifier || '',
      },
    ] : []),
  ]

  return checks.some(check => check.value !== check.original)
}

// TODO: Clean up logic and clarify variable names
const checkUpload = () => {
  okToUpload.value = false

  // Check if this is an upload or an edit modal
  const shouldCheckForFiles = !props.itemToEdit?.uuid
  // Check if this is a clinical report
  const shouldCheckSpecimenType = isPathology.value || isRadiology.value || isGenomics.value

  // Check if any of the input values have changed if editing
  if (!shouldCheckForFiles && !valuesHaveChanged(shouldCheckSpecimenType)) { return }

  const hasDescriptionAndEventDate = description.value.length > 0 && eventDate.value.length > 0
  const hasValidSpecimenType = !shouldCheckSpecimenType || specimenType.value.length > 0
  const hasValidFiles = !shouldCheckForFiles || (typeof files.value[0] !== 'undefined')

  if (hasDescriptionAndEventDate && hasValidSpecimenType && hasValidFiles) {
    okToUpload.value = true
  }
}

const editFile = async () => {
  const result = await editReportFile(
    props.itemToEdit,
    moment(new Date(eventDate.value)).format(),
    typeSelect.value,
    description.value,
    accessionNum.value,
    labName.value,
    specimenType.value,
  )
  if (result.ok) {
    emit('refresh-diagnostics-tab', [typeSelect.value, props.itemToEdit.report_type.short_name])
    emit('update-list', true)
    emit('close-upload-form', false)
    clearInputs()
  }
}

const getReportTypes = async (reportTypesBaseUrl) => {
  try {
    const fetchParams = {
      method: 'GET',
      headers: {
        'Authorization': 'Bearer ' + store.getters.jwt,
      },
    }
    const url = `${reportTypesBaseUrl}`
    const response = await fetchWithTimeout(url, fetchParams)
    if (!response.ok) {
      console.log(`error: status=${response.status}`)
      return []
    }
    const result = await response.json()
    if (!result) { return [] }
    // Translate the response to the format expected by the frontend.
    const tempValues = result.types
    const returnValues = []
    for (let i = 0; i < result.types.length; i++) {
      if (!tempValues[i].voided) {
        returnValues.push(tempValues[i].short_name)
      }
    }
    typeSelect.value = returnValues[0]
    return returnValues
  } catch (e) {
    if (e.name === 'AbortError') {
      console.log('timeout error')
    } else {
      console.log('ERROR:', e)
    }
  }
  return []
}

const uploadFiles = async (files, murl, patientId) => {
  return await Promise.all(files.map((file) => uploadReportFile(file, murl, patientId)))
}


const uploadReportFile = async (file, metadataURL, patientId) => {
  try {
    const formData = new FormData()
    const blob = await fetch(file.url).then(r => r.blob())
    formData.append('report_multipart', blob, file.file.name)
    const isBase64 =
      file.file && file.file.type === 'text/plain' ? false : true

    const fetchParams = {
      method: 'POST',
      headers: {
        'Authorization': 'Bearer ' + store.getters.jwt,
        accept: 'application/json, text/plain, */*',
        'content-type': 'application/json',
      },
      body: JSON.stringify({
        'uuid': file.id,
        'report_type': typeSelect.value,
        'mimetype': file.file.type,
        'identifiers': [
          {
            'identifier': accessionNum.value,
            'identifier_type': 'accession_num',
          },
          {
            'identifier': specimenType.value,
            'identifier_type': 'specimen_type',
          },
          {
            'identifier': labName.value,
            'identifier_type': 'lab_name',
          },
        ],
        'description': description.value,
        'base64': isBase64,
        'ordering_date': moment(new Date(eventDate.value)).format(),
      }),
    }

    const fetchParams2 = {
      method: 'POST',
      headers: {
        'Authorization': 'Bearer ' + store.getters.jwt,
        accept: 'application/json, text/plain, */* ',
      },
      body: formData,
    }
    const murl = `${metadataURL}${patientId}`
    const response = await fetchWithTimeout(murl, fetchParams)
    if (!response.ok) {
      console.log(`error: status=${response.status}`)
      return []
    }
    const result = await response.json()
    if (!result) { return [] }
    // Translate the response to the format expected by the frontend.
    const regexExp = /^[0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12}$/gi
    if (regexExp.test(result.uuid)) {
      //  *************** upload file ************************

      const url = `${ehrCrudUrl}/report-store/report/${file.id}/doc`
      const response = await fetchWithTimeout(url, fetchParams2)
      if (!response.ok) {
        console.log(`error: status=${response.status}`)
        showToast('error', 'Error uploading file')
        return []
      }
      const result2 = await response.json()
      if (!result2) {
        showToast('error', 'Error uploading file')
        return []
      }

      showToast('success', 'File uploaded successfully')
      emit('refresh-diagnostics-tab', [typeSelect.value])
      clearInputs()
    } else {
      showToast('error', result.msg)
    }
    emit('update-list', true)
    return result
  } catch (e) {
    if (e.name === 'AbortError') {
      console.log('timeout error')
    } else {
      console.log('ERROR:', e)
    }
    showToast('error', `Error uploading file: ${e}`)
  }
  return []
}

const clearInputs = () => {
  specimenType.value = ''
  description.value = ''
  eventDate.value = ''
  accessionNum.value = ''
  labName.value = ''
  okToUpload.value = false
  files.value = []
}

const onInputChange = (e) => {
  addFiles(e.target.files)
  e.target.value = null
}

const addFiles = (fileList) => {
  const newFiles = Array.from(fileList)
  const newUploadableFiles = [...newFiles]
    .filter((file) => file.size <= fileSizeLimit || (showToast('error', `File size must be less than ${fileSizeLimit / 1000000}MB`), false))
    .map((file) => createUploadableFile(file))
    .filter((file) => !fileExists(file.id))
    .filter((file) => file.file.type === 'application/pdf' || file.file.type === 'text/plain')

  files.value = files.value.concat(newUploadableFiles)

  if (newFiles.length > files.value.length) { alertFileType() }

  checkUpload()

  // New file preview is last file uploaded
  activePreviewIndex.value = files.value.length - 1

  // Need to reset the input value to allow for reuploading of the same file
  if (fileInputRef.value) {
    fileInputRef.value.value = ''
  }
}

const fileExists = (otherId) => {
  return files.value.some(({ id }) => id === otherId)
}

const removeFile = (file) => {
  const index = files.value.indexOf(file)
  if (index > -1) { files.value.splice(index, 1) }
  // Readjust the preview file's index because the array length as changed
  activePreviewIndex.value = activePreviewIndex.value > 0 ? activePreviewIndex.value -= 1 : 0
  checkUpload()
}

const createUploadableFile = (file) => {
  return {
    file: file,
    id: self.crypto.randomUUID(),
    url: URL.createObjectURL(file),
    status: null,
  }
}

const alertFileType = () => wrongFileType.value = true

onMounted(async () => {
  const typesResult = reactive(await getReportTypes(reportTypesBaseUrl))
  for (const key in typesResult) {
    reportTypes.push(typesResult[key])
  }
  changeType(typeSelect)
})

</script>

<style lang='scss' scoped>
.modal-container {
  position: absolute;
  z-index: 100;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: var(--Black);
  background-color: var(--White);
  width: 600px;

  &.preview {
    display: flex;
    flex-direction: column;
    height: 85%;
    width: 90%;
    max-width: 1400px;
  }
}

.header-text {
  font-size: 1rem;
  color: var(--Grey800);
}

.left {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.right {
  display: block;
  position: relative;
}

.preview-nav-button {
  cursor: pointer;
  user-select: none;
  font-size: 2.4rem;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: solid 2px black;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: white;
  position: absolute;
  z-index: 99999;

  &.chevron-left {
    top: 50%;
    left: 8px;
  }

  &.chevron-right {
    top: 50%;
    right: 8px;
  }
}

.modal-body {
  gap: 2rem;
  padding: 20px;
  overflow: scroll;
  height: 100%;

  &.preview {
    display: grid;
    grid-template-columns: minmax(200px, 1.5fr) minmax(300px, 3fr);
  }
}

.hidden-input {
  display: none;
}

.input-block {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%
}

.submit-block {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  width: 100%;

  button {
    margin-bottom: 0;
  }
}

.close {
  cursor: pointer;
}

label {
  display: flex;
  align-items: center;
  justify-content: space-between;
  text-align: left;
}

select {
  font-family: 'Roboto';
  width: 160px;
}

input {
  font-family: 'Roboto';
  width: 160px;
}

.upload-tips {
  font-size: 0.8rem;
  color: var(--Grey600);
  width: 100%;
  text-align: center;

  &.highlight {
    font-size: 0.8rem;
    color: var(--ErrorRedDark);
    font-weight: bold;
    animation: shake 2s ease-in-out;
  }
}

.upload-box {
  width: 100%;
  background-color: var(--Grey100);
  border: 8px dashed var(--Grey300);
  padding: 20px;
  margin: 10px auto;
  transition: all linear 200ms;
  text-align: center;

  label {
    justify-content: center;
  }

  .upload-box-text {
    color: var(--Grey400);
    font-size: 1.1rem;
  }

  &:hover {
    background-color: var(--Grey200);
    border: 8px dashed var(--Grey500);

    .upload-box-text {
      color: var(--Grey600);
      font-size: 1.1rem;
    }
  }
}

.image-list {
  max-height: 150px;
  overflow-y: auto;
}

input[type=file]:not(:focus-visible) {
  // Visually Hidden Styles taken from Bootstrap 5
  position: absolute !important;
  width: 1px !important;
  height: 1px !important;
  padding: 0 !important;
  margin: -1px !important;
  overflow: hidden !important;
  clip: rect(0, 0, 0, 0) !important;
  white-space: nowrap !important;
  border: 0 !important;
}

ul {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0;
}

@keyframes shake {
  0% {
    transform: translateX(0);
  }

  10%,
  90% {
    transform: translateX(-5px);
  }

  20%,
  80% {
    transform: translateX(5px);
  }

  30%,
  50%,
  70% {
    transform: translateX(-5px);
  }

  40%,
  60% {
    transform: translateX(5px);
  }

  100% {
    transform: translateX(0);
  }
}
</style>
